import { Icon as BlueIcon } from '@blueprintjs/core';
import { useState } from '@hookstate/core';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useState as classicUseState,
} from 'react';

import { ITag } from '@models/Tag.model';

import { WindowButton } from '@components/Filters/FilterProduct/components/FilterProductContent/FilterProductContent.styles';
import {
  Count,
  CountNumber,
  RowDiv,
  RowDivPointer,
} from '@pages/Products/Products.styles';
import { getEquipmentsByTag } from '@services/queries/equipments';
import { useMutation } from 'react-query';

import { IPaginationResult } from '@models/Equipment.model';
import { IEquipmentInventory } from '@models/EquipmentInventory.model';
import { ListEquipmentsTag } from './components/ListEquipmentsTag/ListEquipmentsTag';
import { TagsDetailsMenu } from './components/TagsDetailsMenu/TagsDetailsMenu';
import {
  Container,
  Divider,
  Head,
  Label,
  LineDivider,
  Tag,
  TagsText,
  Text,
  WrapperRowButton,
} from './TagInfoBody.styles';

interface IHelpBody {
  onClose: () => void;
  // Todo:  simplify this.
  tag: Pick<ITag, 'id' | 'name' | 'description'> & {
    tagCategory?: Pick<NonNullable<ITag['tagCategory']>, 'name'>;
  };
  setTopic: Dispatch<SetStateAction<'user' | null>>;
}

const TagInfoBody = ({ tag, onClose, setTopic }: IHelpBody) => {
  const [page, setPage] = classicUseState(1);
  const [loadingSearch, setLoadingSearch] = classicUseState(false);
  const [equipmentData, setEquipmentData] = classicUseState<
    IPaginationResult<IEquipmentInventory> | undefined
  >(undefined);

  const [openDetails, setOpenDetails] = classicUseState<boolean>(false);

  const inventory = useState<
    'software' | 'miscellaneous' | 'services' | 'equipment'
  >('equipment');

  const getEquipmentsWithSearch = useMutation(getEquipmentsByTag, {
    onSuccess: newData => {
      if (inventory.get() === 'equipment') {
        setLoadingSearch(false);
        setPage(newData.currentPage);
      }
      if (equipmentData && newData.currentPage > 1) {
        const resultsContacted = equipmentData.results.concat(newData.results);
        setEquipmentData({
          total: newData.total,
          currentPage: newData.currentPage,
          totalPages: newData.totalPages,
          hasMore: newData.hasMore,
          results: resultsContacted,
        });
      } else setEquipmentData(newData);
    },
    onError: () => {
      setLoadingSearch(false);
    },
  });
  useEffect(() => {
    setLoadingSearch(true);
    getEquipmentsWithSearch.mutate({
      pagination: { page: 1, limit: 12 },
      filter: {
        tagId: tag.id,
      },
    });
  }, [tag]);
  //
  const startSearch = (
    sort?: string,
    direction?: 'ASC' | 'DESC' | undefined,
  ) => {
    setPage(1);
    setLoadingSearch(true);

    getEquipmentsWithSearch.mutate({
      pagination: {
        page: 1,
        limit: 12,
      },
      filter: {
        tagId: tag.id,
        sort,
        direction,
      },
    });
  };

  const loadMore = () => {
    if (inventory.get() === 'equipment' && equipmentData?.hasMore) {
      getEquipmentsWithSearch.mutate({
        pagination: {
          page: page + 1,
          limit: 12,
        },
        filter: {
          tagId: tag.id,
        },
      });
    }
  };

  return (
    <Container>
      <Head style={{ marginBottom: 16 }}>
        <Tag>
          {tag.tagCategory && tag.tagCategory.name !== 'Uncategorized' && (
            <Text>{`${tag.tagCategory.name}: `}</Text>
          )}
          <TagsText>{tag.name}</TagsText>
        </Tag>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <TagsDetailsMenu tag={tag} onClose={onClose} />

          <WindowButton
            onClick={() => {
              onClose();
              setTopic(null);
            }}
          >
            <BlueIcon icon="cross" color="#8F95B2" />
          </WindowButton>
        </div>
      </Head>
      <LineDivider />
      {tag.description && (
        <>
          <WrapperRowButton onClick={() => setOpenDetails(!openDetails)}>
            <Label className="title">{tag.name} Details</Label>
            <Divider width="8px" />
            {openDetails ? (
              <BlueIcon icon="chevron-down" color="#8F95B2" />
            ) : (
              <BlueIcon icon="chevron-right" color="#8f95b2" />
            )}
          </WrapperRowButton>

          {openDetails && (
            <>
              <Divider height="8px" />
              <Label>{`${tag.description}`}</Label>
            </>
          )}
        </>
      )}

      <Divider height="24px" />

      {equipmentData && !loadingSearch && (
        <RowDiv>
          <RowDivPointer
            onClick={() => {
              inventory.set('equipment');
            }}
          >
            <Count>Equipment</Count>
            <CountNumber>{`${
              equipmentData?.total ? equipmentData.total : 0
            }`}</CountNumber>
          </RowDivPointer>
        </RowDiv>
      )}
      <div style={{ height: 16 }} />
      {inventory.get() === 'equipment' && (
        <ListEquipmentsTag
          data={equipmentData?.results}
          isLoading={loadingSearch}
          isError={false}
          loadMore={() => loadMore()}
          hasMore={equipmentData?.hasMore}
          startSearch={startSearch}
          onClose={onClose}
        />
      )}
    </Container>
  );
};

export { TagInfoBody };
